import {
  findElementByText,
  normalizeText,
  replaceText,
  setVisible,
} from "./domUtils";

const EMPLOYEE_FORM_TABS = [
  { id: "personal", label: "Dados pessoais" },
  { id: "corporate", label: "Dados corporativos" },
  { id: "education", label: "Formação" },
  { id: "financial", label: "Financeiro" },
  { id: "address", label: "Endereço" },
  { id: "files", label: "Arquivos" },
  { id: "system", label: "Configurações do Sistema" },
];

export const installEmployeeTabStyles = () => {
  if (document.getElementById("human-resources-employee-tabs-style")) return;
  const style = document.createElement("style");
  style.id = "human-resources-employee-tabs-style";
  style.textContent = `
    [data-hr-employee-tabs] { display:flex; gap:.5rem; overflow-x:auto; padding:.75rem; border:1px solid #e2e8f0; border-radius:1rem; background:#f8fafc; scrollbar-width:thin; }
    [data-hr-employee-tabs] button { flex:0 0 auto; border:1px solid #cbd5e1; border-radius:9999px; background:#fff; color:#475569; padding:.55rem .9rem; font-size:.78rem; font-weight:700; transition:.15s ease; }
    [data-hr-employee-tabs] button[aria-selected="true"] { border-color:#059669; background:#059669; color:#fff; box-shadow:0 4px 12px rgba(5,150,105,.2); }
    [data-hr-photo-name-grid] > [data-hr-photo-field] { order:-2; }
    [data-hr-photo-name-grid] > [data-hr-name-field] { order:-1; }
    [data-hr-employee-title] { display:inline-flex; align-items:center; gap:.5rem; }
    [data-hr-employee-title-icon] { width:1.25rem; height:1.25rem; flex:none; color:#059669; }
    @media (min-width:768px) { [data-hr-photo-name-grid] { grid-template-columns:minmax(230px,.72fr) minmax(0,2fr)!important; } }
  `;
  document.head.appendChild(style);
};

const sectionTitle = (section) =>
  normalizeText(section.querySelector("h3")?.textContent || "");

const setTabContentVisible = (element, visible) => {
  if (!element) return;
  setVisible(element, visible);
  element
    .querySelectorAll("input, select, textarea, button")
    .forEach((control) => {
      if (!visible) {
        if (!control.disabled) {
          control.dataset.hrDisabledByInactiveTab = "true";
          control.disabled = true;
        }
        return;
      }
      if (control.dataset.hrDisabledByInactiveTab === "true") {
        control.disabled = false;
        delete control.dataset.hrDisabledByInactiveTab;
      }
    });
};

const configurePhotoFirst = (section) => {
  const photoInput = section?.querySelector('[name="photoUrl"]');
  const nameInput = section?.querySelector('[name="fullName"]');
  if (!photoInput || !nameInput) return;

  const photoField =
    photoInput.closest("div.rounded-xl") || photoInput.parentElement?.parentElement;
  const nameField = nameInput.closest("div");
  const sharedGrid = photoField?.parentElement;
  if (!photoField || !nameField || !sharedGrid || nameField.parentElement !== sharedGrid) return;

  sharedGrid.dataset.hrPhotoNameGrid = "true";
  photoField.dataset.hrPhotoField = "true";
  nameField.dataset.hrNameField = "true";
};

const configurePersonalSection = (section, activeTab) => {
  const header = section.firstElementChild;
  const content = header?.nextElementSibling;
  if (!content) return;
  configurePhotoFirst(section);

  let hasVisible = false;
  Array.from(content.children).forEach((block) => {
    const isSystemAccess = Boolean(block.querySelector('[name="userId"]'));
    const visible = isSystemAccess
      ? activeTab === "system"
      : activeTab === "personal";
    setTabContentVisible(block, visible);
    if (visible) hasVisible = true;
  });
  setVisible(header, activeTab === "personal");
  setVisible(section, hasVisible);
};

const configureCostSection = (section, activeTab) => {
  setTabContentVisible(section, activeTab === "financial");
  replaceText(section.querySelector("h3"), "Financeiro");
};

const installEmployeeTitleIcon = (heading) => {
  if (!heading) return;

  heading.dataset.hrEmployeeTitle = "true";
  const existingIcon = heading.querySelector("[data-hr-employee-title-icon]");
  if (existingIcon) {
    if (heading.firstChild !== existingIcon) {
      heading.insertBefore(existingIcon, heading.firstChild);
    }
    return;
  }

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.dataset.hrEmployeeTitleIcon = "true";
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "currentColor");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");

  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute(
    "d",
    "M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.5 20.118a7.5 7.5 0 0 1 15 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.5-1.632Z",
  );
  svg.appendChild(path);
  heading.insertBefore(svg, heading.firstChild);
};

export const configureEmployeeFormTab = (form, activeTab) => {
  form.dataset.hrActiveEmployeeTab = activeTab;
  Array.from(form.querySelectorAll(":scope > section")).forEach((section) => {
    const title = sectionTitle(section);
    if (title === "dados pessoais") return configurePersonalSection(section, activeTab);
    if (title === "formacao") return setTabContentVisible(section, activeTab === "education");
    if (title === "dados corporativos") {
      return setTabContentVisible(section, activeTab === "corporate");
    }
    if (["custo financeiro", "custo", "financeiro"].includes(title)) {
      return configureCostSection(section, activeTab);
    }
    if (title === "endereco") {
      return setTabContentVisible(section, activeTab === "address");
    }
    if (title === "contato de emergencia") {
      return setTabContentVisible(section, activeTab === "personal");
    }
    if (title === "arquivos do colaborador") {
      return setTabContentVisible(section, activeTab === "files");
    }
    if (title.includes("acesso ao sistema")) {
      replaceText(
        section.querySelector("h3"),
        "Configurações do Sistema — Acesso ao Sistema",
      );
      return setTabContentVisible(section, activeTab === "system");
    }
  });

  form.querySelectorAll("[data-hr-employee-tabs] button").forEach((button) => {
    button.setAttribute(
      "aria-selected",
      String(button.dataset.hrEmployeeTab === activeTab),
    );
  });
};

export const installEmployeeFormTabs = () => {
  const heading = findElementByText("h2", (text) =>
    text === "editar colaborador" || text === "novo colaborador",
  );
  if (!heading) return;

  installEmployeeTitleIcon(heading);

  const modalContainer =
    heading.closest('[role="dialog"]') ||
    heading.closest("div.space-y-6") ||
    heading.parentElement?.parentElement;
  const form = modalContainer?.querySelector("form");
  if (!form) return;

  let navigation = form.querySelector("[data-hr-employee-tabs]");
  if (!navigation) {
    navigation = document.createElement("nav");
    navigation.dataset.hrEmployeeTabs = "true";
    navigation.setAttribute("aria-label", "Seções do cadastro do colaborador");
    EMPLOYEE_FORM_TABS.forEach((tab) => {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.hrEmployeeTab = tab.id;
      button.textContent = tab.label;
      button.setAttribute("aria-selected", String(tab.id === "personal"));
      button.addEventListener("click", () =>
        configureEmployeeFormTab(form, tab.id),
      );
      navigation.appendChild(button);
    });
    form.insertBefore(navigation, form.firstElementChild);
  }

  configureEmployeeFormTab(
    form,
    form.dataset.hrActiveEmployeeTab || "personal",
  );
};
